import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import ReportButton from '../../../../components/Buttons/ReportButton'
import useFetch from '../../../../hooks/fetchMSSQL-hook'
import { NotificationManager } from 'react-notifications'
import ExportWarningModal from '../../../../components/Modals/ExportWarningModal'

export default function ProductsTable({
	products = [],
	API_URL,
	openForm,
	sampleProduct,
	sortBy = "none",
	handleHeaderSort = () => {},
	depositTypes = [],
	showAddButton = true,
	addButtonText = "Dodaj produkt",
}) {
	const exportWarningStorageKey = "hideImportExportWarning"

	const getSortState = () => {
		const [field, dir] = String(sortBy || "").split("|")
		return { field, dir: (dir || "").toUpperCase() }
	}

	const sortState = getSortState()

	const arrow = (field) => {
		if (sortState.field !== field) return ""
		return sortState.dir === "ASC" ? " ▲" : " ▼"
	}

	const { fetchMssqlApi } = useFetch()
	const importInputRef = useRef(null)
	const [isImporting, setIsImporting] = useState(false)
	const [isExporting, setIsExporting] = useState(false)
	const [showExportWarning, setShowExportWarning] = useState(false)

	const depositById = useMemo(() => {
		const map = new Map()

		depositTypes.forEach((deposit) => {
			map.set(String(deposit.DepositId), deposit)
		})

		return map
	}, [depositTypes])

	const formatPLN = (value) => {
		const numberValue = Number(value)

		if (!Number.isFinite(numberValue)) return ""

		return `${numberValue.toFixed(2).replace('.', ',')} zł`
	}

	const renderDeposit = (product) => {
		if (!product || product.IsIngredient) return ""

		const id = product.DepositId

		if (id === null || id === undefined || String(id) === "" || String(id) === "0") {
			return ""
		}

		const deposit = depositById.get(String(id))

		if (deposit) {
			return `${deposit.Name} (${formatPLN(deposit.Price)})`
		}

		if (product.DepositName) {
			const price = product.DepositPrice

			return Number.isFinite(Number(price))
				? `${product.DepositName} (${formatPLN(price)})`
				: product.DepositName
		}

		return `ID: ${id}`
	}

	const getErrorMessage = (error, fallbackMessage) => {
		const data = error?.response?.data || error?.data || error || {}
		return data?.message || error?.message || fallbackMessage
	}

	const exportProducts = () => {
		if (isExporting) return

		setIsExporting(true)

		try {
			fetchMssqlApi(
				"/products/export",
				{ method: "GET", responseType: "blob", hideNotification: true },
				(response) => {
					const responseData = response?.data ?? response
					const blob = responseData instanceof Blob
						? responseData
						: new Blob([responseData], {
							type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
						})
					const url = window.URL.createObjectURL(blob)
					const link = document.createElement("a")

					link.href = url
					link.download = "products.xlsx"
					document.body.appendChild(link)
					link.click()
					link.remove()
					window.URL.revokeObjectURL(url)

					setIsExporting(false)
				},
				(error) => {
					NotificationManager.error(
						getErrorMessage(error, "Nie udało się wyeksportować produktów"),
						"Eksport",
						10000
					)
					setIsExporting(false)
				}
			)
		} catch (error) {
			NotificationManager.error(
				getErrorMessage(error, "Nie udało się wyeksportować produktów"),
				"Eksport",
				10000
			)
			setIsExporting(false)
		}
	}

	const handleExportClick = () => {
		try {
			if (localStorage.getItem(exportWarningStorageKey) === "true") {
				exportProducts()
				return
			}
		} catch (err) {
			console.log(err)
		}

		setShowExportWarning(true)
	}

	const closeExportWarning = () => {
		setShowExportWarning(false)
	}

	const continueExportWarning = () => {
		setShowExportWarning(false)
		exportProducts()
	}

	const getImportErrorMessage = (error) => {
		const data = error?.response?.data || error?.data || error || {}
		const errors = data?.errors || error?.errors

		if (Array.isArray(errors) && errors.length) {
			const visibleErrors = errors.slice(0, 6).map((err) => {
				const row = err.RowNumber ? `Wiersz ${err.RowNumber}` : "Plik"
				const field = err.FieldName ? `, ${err.FieldName}` : ""
				const message = err.Message || err.message || "Błąd walidacji"

				return `${row}${field}: ${message}`
			})

			if (errors.length > visibleErrors.length) {
				visibleErrors.push(`I jeszcze ${errors.length - visibleErrors.length} błędów`)
			}

			return visibleErrors.join("\n")
		}

		return data?.message || error?.message || "Nie udało się zaimportować pliku"
	}

	const finishImport = (input) => {
		if (input) {
			input.value = ""
		}

		setIsImporting(false)
	}

	const handleImportClick = () => {
		if (!isImporting && importInputRef.current) {
			importInputRef.current.click()
		}
	}

	const handleImportChange = (evt) => {
		const input = evt.target
		const file = input.files && input.files[0]

		if (input) {
			input.value = ""
		}

		if (!file || isImporting) return

		const ext = file.name.split(".").pop()?.toLowerCase()

		if (ext !== "xlsx") {
			NotificationManager.error("Wybierz plik .xlsx", "Import")
			return
		}

		const formData = new FormData()
		formData.append("file", file)

		setIsImporting(true)

		try {
			fetchMssqlApi(
				"/products/import",
				{ method: "POST", data: formData, hideNotification: true },
				(res) => {
					NotificationManager.success(
						`Zaktualizowano: ${res?.UpdatedCount || 0}, dodano: ${res?.InsertedCount || 0}`,
						"Import"
					)

					finishImport(input)

					setTimeout(() => {
						window.location.reload()
					}, 500)
				},
				(err) => {
					NotificationManager.error(
						getImportErrorMessage(err),
						"Import",
						10000
					)

					finishImport(input)
				}
			)
		} catch (err) {
			NotificationManager.error(
				getImportErrorMessage(err),
				"Import",
				10000
			)

			finishImport(input)
		}
	}

	return (
		<>
			<div className="border">
				<input
					ref={importInputRef}
					type="file"
					accept=".xlsx"
					disabled={isImporting}
					onChange={handleImportChange}
					style={{ display: "none" }}
				/>

				<div className="d-flex justify-content-end">
					<button
						type="button"
						className="d-block btn btn-link text-decoration-none my-2 mr-2"
						disabled={isExporting}
						onClick={handleExportClick}
					>
						<i className="fas fa-file-export mr-2" />
						{isExporting ? "Export..." : "Export"}
					</button>

					<button
						type="button"
						className="d-block btn btn-link text-decoration-none my-2 mr-2"
						disabled={isImporting}
						onClick={handleImportClick}
					>
						<i className="fas fa-file-import mr-2" />
						{isImporting ? "Import..." : "Import"}
					</button>
				</div>

				<div className="custom-scroll overflow-auto" style={{ maxHeight: "calc(100vh - 170px)" }}>
					<table className="table table-striped">
						<thead className="headers-stick-trxs">
							<tr className="text-center headers-color">
								{process.env.REACT_APP_LOCAL_DB === "true" ? (
									<>
										<th className="text-center">Zdjęcie</th>
										<th className="text-center">EAN</th>
										<th className="text-center">Nazwa</th>
										<th className="text-center">Opis</th>
										<th className="text-center">VAT</th>
										<th className="text-center">Kaucja</th>
										<th className="p-2 align-middle" style={{ width: "90px" }}>
											{showAddButton && (
												<div className="d-flex flex-column align-items-center">
													<i
														className="btn btn-outline-primary fas fa-plus"
														title={addButtonText}
														aria-label={addButtonText}
														role="button"
														onClick={openForm}
													/>
												</div>
											)}
										</th>
									</>
								) : (
									<>
										<th
											className="text-center"
											style={{ width: "90px", cursor: "pointer", userSelect: "none" }}
											onClick={() => handleHeaderSort("Image")}
										>
											Zdjęcie{arrow("Image")}
										</th>
										<th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("EAN")}>
											EAN{arrow("EAN")}
										</th>
										<th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("Name")}>
											Nazwa{arrow("Name")}
										</th>
										<th className="text-center" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("IsActive")}>
											Aktywny{arrow("IsActive")}
										</th>
										<th className="text-center" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("IsIngredient")}>
											Składnik{arrow("IsIngredient")}
										</th>
										<th className="text-center" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("IngredientTypeName")}>
											Typ składnika{arrow("IngredientTypeName")}
										</th>
										<th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("IngredientQuantity")}>
											Ilość składnika{arrow("IngredientQuantity")}
										</th>
										<th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("VATName")}>
											Stawka VAT{arrow("VATName")}
										</th>
										<th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("DepositName")}>
											Kaucja{arrow("DepositName")}
										</th>
										<th />
										<th className="p-2 align-middle" style={{ width: "90px" }}>
											{showAddButton && (
												<div className="d-flex flex-column align-items-center">
													<i
														className="btn btn-outline-primary fas fa-plus"
														title={addButtonText}
														aria-label={addButtonText}
														role="button"
														onClick={openForm}
													/>
												</div>
											)}
										</th>
									</>
								)}
							</tr>
						</thead>

						<tbody>
							{products.map((product, idx) => (
								<tr className="text-center" key={product.ProductId ?? product.EAN ?? idx}>
									<td>
										<img
											src={`${API_URL}/${product.Image}`}
											onError={(e) => (e.currentTarget.src = sampleProduct)}
											loading="lazy"
											alt={product.Name}
											width="48px"
											height="48px"
										/>
									</td>
									<td style={{ wordBreak: "break-word" }}>{product.EAN}</td>
									<td style={{ wordBreak: "break-word" }}>{product.Name}</td>

									{process.env.REACT_APP_LOCAL_DB === "true" ? (
										<>
											<td style={{ wordBreak: "break-word" }}>{product.Description}</td>
											<td style={{ wordBreak: "break-word" }}>{product.VatPercent}</td>
											<td style={{ wordBreak: "break-word" }}>{product.Deposit}</td>
										</>
									) : (
										<>
											<td className="p-1 text-center text-primary" style={{ minWidth: "150px" }}>
												<i className={product.IsActive ? "fas fa-check-square" : "far fa-square"} />
											</td>
											<td className="p-1 text-center text-primary" style={{ minWidth: "150px" }}>
												<i className={product.IsIngredient ? "fas fa-check-square" : "far fa-square"} />
											</td>
											<td>{product?.IngredientTypeName ?? product?.IngredientType ?? ""}</td>
											<td>{product?.IngredientQuantity}</td>
											<td>{product?.VATName}</td>
											<td>{renderDeposit(product)}</td>
											<td>
												<ReportButton
													reportId={100}
													btnColor="primary"
													body={() => ({ productIds: `[${product.ProductId}]` })}
												/>
											</td>
										</>
									)}

									<td>
										<Link className="btn btn-outline-primary btn-sm" to={`product/${product.EAN}`}>
											Edytuj
										</Link>
									</td>
								</tr>
							))}
						</tbody>
					</table>

					{products.length === 0 && (
						<div className="text-center p-3 text-muted">
							Brak wyników
						</div>
					)}
				</div>
			</div>

			<ExportWarningModal
				show={showExportWarning}
				storageKey={exportWarningStorageKey}
				onClose={closeExportWarning}
				onConfirm={continueExportWarning}
			/>
		</>
	)
}